import { CamFilterValues, Camera, CameraGroup } from '@hakimo-ui/hakimo/types';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { MutableRefObject } from 'react';
import { DEFAULT_CAM_FILTER } from '../constants';
import { GridSize, MAX_SELECTED_CAMERAS } from './util';

export const CAMERAS_TAB_ID = 'selected-cameras';
export const DROP_ZONE_ID = 'multi-cam-drop-zone';

export const multiCamFilterAtom = atomWithStorage<CamFilterValues>(
  'multiCamFilters',
  DEFAULT_CAM_FILTER
);

export interface MultiCamGroupTab {
  id: string;
  type: 'group';
  name: string;
  cameraGroupId: string;
}

export type MultiCamTab = MultiCamGroupTab | { id: string; type: 'cameras' };

export type DragItem =
  | { type: 'camera'; camera: Camera; checked: boolean }
  | { type: 'group'; group: CameraGroup; checked: boolean };

export const committedCameraMapAtom = atom<Record<string, Camera>>({});
export const committedGroupMapAtom = atom<Record<string, CameraGroup>>({});
export const removedFromGroupAtom = atom<Record<string, string[]>>({});

// Tabs are derived from the committed selection. The cameras tab (present when
// any camera is committed) always comes first, followed by one tab per group.
export const tabsAtom = atom<MultiCamTab[]>((get) => {
  const groups = get(committedGroupMapAtom);
  const cameras = get(committedCameraMapAtom);
  const groupTabs: MultiCamTab[] = Object.values(groups).map((group) => ({
    id: group.cameraGroupId,
    type: 'group',
    name: group.name,
    cameraGroupId: group.cameraGroupId,
  }));
  const cameraTab: MultiCamTab[] =
    Object.keys(cameras).length > 0
      ? [{ id: CAMERAS_TAB_ID, type: 'cameras' }]
      : [];
  return [...cameraTab, ...groupTabs];
});

export const activeTabIdAtom = atom<string | null>(null);
export const activeTabAtom = atom<MultiCamTab | null>((get) => {
  const tabs = get(tabsAtom);
  const activeTabId = get(activeTabIdAtom);
  return tabs.find((tab) => tab.id === activeTabId) ?? tabs[0] ?? null;
});
// Set when a selection could not be fully applied because it would exceed
// MAX_SELECTED_CAMERAS, so the limit modal can be shown from any commit path.
export const cameraLimitAlertOpenAtom = atom<boolean>(false);
// Manual grid-size override (rows x cols) per tab (keyed by tab id). A missing
// entry means the size is derived from the camera count (Auto).
export const gridSizeOverrideAtom = atom<Record<string, GridSize | null>>({});
// The element the page is fullscreened into, or null when not fullscreen.
// Modals opened from inside the grid portal here so they are not rendered
// outside the fullscreen subtree.
export const fullScreenTargetAtom =
  atom<MutableRefObject<HTMLElement | null> | null>(null);
// Manual camera order per tab (keyed by tab id), holding ordered camera ids.
// Cameras missing from the list keep their natural order after the ordered
// ones. Persisted so a reordered grid survives reloads.
export const gridOrderAtom = atomWithStorage<Record<string, string[]>>(
  'multiCamGridOrder',
  {}
);

// Single write-only owner of every selection add. Reading current state here
// (instead of smuggling a read into a set-updater) keeps the commit atomic.
// The cap is per tab: the cameras tab holds up to MAX_SELECTED_CAMERAS
// individual cameras, and each group tab is capped where it renders, so an
// oversized group is still added but raises the limit alert.
export const commitSelectionAtom = atom(
  null,
  (get, set, cameras: Camera[], groups: CameraGroup[]) => {
    const prevCameras = get(committedCameraMapAtom);
    const prevGroups = get(committedGroupMapAtom);

    const nextCameras = { ...prevCameras };
    let addedCamera = false;
    let overflowed = false;
    cameras.forEach((cam) => {
      if (nextCameras[cam.id]) return;
      if (Object.keys(nextCameras).length >= MAX_SELECTED_CAMERAS) {
        overflowed = true;
        return;
      }
      nextCameras[cam.id] = cam;
      addedCamera = true;
    });

    const nextGroups = { ...prevGroups };
    let lastAddedGroupId: string | null = null;
    groups.forEach((group) => {
      if (nextGroups[group.cameraGroupId]) return;
      if (group.cameraCount > MAX_SELECTED_CAMERAS) overflowed = true;
      nextGroups[group.cameraGroupId] = group;
      lastAddedGroupId = group.cameraGroupId;
    });

    if (overflowed) set(cameraLimitAlertOpenAtom, true);
    if (addedCamera) set(committedCameraMapAtom, nextCameras);
    if (lastAddedGroupId) set(committedGroupMapAtom, nextGroups);
    if (lastAddedGroupId) {
      set(activeTabIdAtom, lastAddedGroupId);
    } else if (addedCamera && Object.keys(prevCameras).length === 0) {
      set(activeTabIdAtom, CAMERAS_TAB_ID);
    }
  }
);
